import { el, mount, text } from "redom"
import { setCollapseActivityList, getShowMoreText, updateToPost, initButtons, resetInnerHtmlForAll } from "../helpers/collapse.js"
import { createRecordDeletionModalElement } from "../components/build-modal.js"
import { getTimeAgo } from "../helpers/time-converter.js"
import { PATH } from "../helpers/shared-record-path.js"
import { RecordsSection } from '../components/records-section.js'
import { type RecordItem, RecordItemWithImage, RecordItemWithoutImage, getFirstValidId } from "../components/record-item.js"
import { fetchGetJson, handleError } from "../../utils/fetch-json.js"
import { getAuthUserWithHandlingError as getAuthUser, maybeMeIsMe } from '../../utils/authUser.js'
import { isBlocked } from "../helpers/report.js"
import type { Activity, ActivityWithUser, AudioRecord, RecentActivity, RecordsWithUser, UserDetails } from "#/types"

const defaultSharedRecordsNumber = 6
const defaultRecentActivitiesNumber = 3
const defaultSubscriptionRecordsNumber = 5
const defaultFeaturedRecordsNumber = 10

const addSection = (type: Constructor<RecordItem>): RecordsSection => {
    const section = new RecordsSection(type)
    const recordsPage = document.getElementById("records-page")
    if (recordsPage) mount(recordsPage, section)
    return section
}

const fillInSection = (title: string, section: RecordsSection, recordsWithUser: RecordsWithUser[], numOfItem: number, collapseName: string, listKeyword: string, hasBorder: boolean) => {
    section.init({ items: recordsWithUser, numOfItem, collapseName })
    section.sectionTitle.updatePlayAllTitle(title, getFirstValidId(recordsWithUser) ?? "", listKeyword, hasBorder)
}

export const displaySection = (title: string, type: Constructor<RecordItem>, recordsWithUser: RecordsWithUser[], numOfItem: number, collapseName: string, listKeyword: string) => {
    const section = addSection(type)
    fillInSection(title, section, recordsWithUser, numOfItem, collapseName, listKeyword, false)
}

// NOTE: change this function's name since it can be complicated with 'Subscription to app'
const subscriptionInit = () => {
    const section = addSection(RecordItemWithImage)
    return getAuthUser().then(maybeMe => {
        if (maybeMe.isDefined)
            return fetchGetJson<RecordsWithUser[]>("/v1/playlist/following").then((result: RecordsWithUser[]) => {
                const recordsWithUser = result.filter((r: RecordsWithUser) => !isBlocked(r.uploader.id))
                fillInSection("YOU FOLLOW", section, recordsWithUser, defaultSubscriptionRecordsNumber, "subscription", "foll", true)
            }).catch(handleError)
    })
}

export const playlistRecordsInit = (typeOfPlaylist: string, numOfItem: number) => {
    const showNoFilesMessage = (section: RecordsSection, title: string) => {
        section.sectionTitle.updateTitle(title, true);
        section.el.style.display = "block";
        mount(section, el("center", el("h4.noMargin", { innerText: "No files to show", style: "padding: 70px 0" })));
    };
    const section = addSection(RecordItemWithImage)
    const title = typeOfPlaylist.toUpperCase() + (typeOfPlaylist == "monthly" ? " HIGHLIGHTS" : " RECORDS")
    return fetchGetJson<RecordsWithUser[]>("/v1/playlist/" + typeOfPlaylist).then((result: RecordsWithUser[]) => {
        // TODO: cleanup this with separating 'featured records' and others
        const recordsWithUser = result.filter((s: RecordsWithUser) => !isBlocked(s.uploader.id))
        if (recordsWithUser.length == 0 && typeOfPlaylist == 'featured') {
            return showNoFilesMessage(section, title)
        }
        fillInSection(title, section, recordsWithUser, numOfItem, typeOfPlaylist, typeOfPlaylist.substr(0, 4), true)
    }).catch(() => { showNoFilesMessage(section, title) })
}

const initRecentActivity = async (): Promise<void> => {
    try {
        const section = addSection(RecordItemWithoutImage)
        const maybeMe = await getAuthUser()
        if (!maybeMeIsMe(maybeMe)) return
        const recent = await fetchGetJson<RecentActivity>("/v1/u/" + maybeMe.get.id + "/recentActivity").catch(handleError)
        if (!recent) return
        const recentActivities = recent.activities.filter((activity: ActivityWithUser) => !isBlocked(activity.user.id))
        const collapseName = "shared"
        const recentRecords = recent.records.map((e: AudioRecord) => { return { file: e, uploader: maybeMe.get } })
        section.init({ items: recentRecords, numOfItem: defaultSharedRecordsNumber, collapseName })
        section.sectionTitle.updateTitle("RECENT ACTIVITY", true)
        mount(section, createRecordDeletionModalElement())
        if (recentRecords.length) mount(section.sc_sec, el("h3.mb-1 border-bottom")) // No need for an extra border if there are no records
        if (recentActivities.length) {
            section.el.style.display = "block"
            section.sc_sec.style.paddingBottom = "0"
            mount(section, buildRecentActivity(recentActivities, maybeMe.get.id, defaultRecentActivitiesNumber))
            setCollapseActivityList(recentActivities.length - defaultRecentActivitiesNumber)
        }
    } catch {
        console.warn("Failed to load recent activity")
        return
    }
}

const mainFeedPageInit = (clicked: boolean) => {
    PATH.addHistory("/records", clicked)
    if (clicked) window.scrollTo(0, 0)
    initButtons()
    resetInnerHtmlForAll()
    document.title = "Featured Records - Soundcorset"
    //Order matters
    initRecentActivity()
    subscriptionInit()
    playlistRecordsInit("featured", defaultFeaturedRecordsNumber)
    playlistRecordsInit("monthly", 5)
    playlistRecordsInit("latest", 5)
}

export const navigateToMainFeed = () => mainFeedPageInit(false)
export const openMainFeedOnClick = () => mainFeedPageInit(true)

const buildRecentActivity = (userWithLike: ActivityWithUser[], myId: string, numOfItem: number) => {
    const buildActivityList = function (activity: Activity, user: UserDetails) {
        const userImg = el("img.rounded-circle sc_activity_list_profile_img", {
            src: user.imgUrl,
            style: "margin-right: 8px",
            onerror: (e: Event) => {
                (e.currentTarget as HTMLImageElement).src = '/public/img/icon200x200.png';
            }
        })
        const activityListHTML = el(".sc_activity_list.clickable",
            [userImg, el("span.sc_activity_list_text_bold", { innerText: user.id == myId ? "You" : user.name }), text(" " + activity.what + " your record "),
                el("span.sc_activity_list_time", { innerText: getTimeAgo(activity.when) })
            ])
        activityListHTML.onclick = () => updateToPost(activity.where)
        return activityListHTML
    }

    const buildRecentHTML = el("", { style: "padding-bottom:10px" })
    userWithLike.slice(0, numOfItem).forEach((activityAndUser: ActivityWithUser) => {
        mount(buildRecentHTML, buildActivityList(activityAndUser.activity, activityAndUser.user))
    })

    if (userWithLike.length > numOfItem) {
        const hiddenRecent = el(".collapse.noSpace#recentActivityCollapse")
        userWithLike.slice(numOfItem).forEach((activityAndUser: ActivityWithUser) => {
            hiddenRecent.appendChild(buildActivityList(activityAndUser.activity, activityAndUser.user))
        })
        buildRecentHTML.appendChild(hiddenRecent)
        buildRecentHTML.appendChild(
            el("a.clickable", { 'data-bs-toggle': "collapse", href: "#recentActivityCollapse", role: "button", 'aria-expanded': "false", 'aria-controls': "recentActivityLikeList" },
                el("p.collapseListItem#recentActivityBtnCollapse", [el("i.fas fa-angle-down collapseIcon"), getShowMoreText("", userWithLike.length - defaultRecentActivitiesNumber)])
            ))
    }

    return buildRecentHTML
}
